import {
  ITableFields,
  TABLE_FIELD_ALIGN,
  TABLE_FIELD_TYPE,
} from "../../core-component/ui/ICTable";
import { IOption, IPagination } from "../../data/AppInterface";
import {
  TBitValue,
  TReactSetState,
  TSetPaginationFn,
  TUseTranslationTfn,
} from "../../data/AppType";
import {
  CANCEL_SUBSCRIPTION,
  GET_ALL_FIRM_PURCHASED_SUBSCRIPTION,
  GLOBAL_DROP_DOWN,
} from "../../services/Endpoints";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import {
  handleError,
  sweetAlertError,
  sweetAlertSuccess,
  toastError,
} from "../../utils/AppFunctions";

export interface IFirmPurchasedSubscription {
  id: string;
  firm_name: string;
  subscription_package_name: string;
  subscribed_by: string;
  subscription_date: Date;
  start_date: Date;
  end_date: Date;
  remaining_expiry_days: string;
  total: string;
}
export interface IFirmPurchasedSubscriptionSearch {
  firmId: string | null;
  showHistoricalRecords: TBitValue;
  year: number | null;
}

export const FIRM_PURCHASED_SUBSCRIPTION_ADVANCE_SEARCH_INITIAL_VALUE = {
  firmId: null,
  showHistoricalRecords: BIT_VALUE.Zero,
  year: null,
};
export function getFirmPurchasedSubscription<T>(
  actionComponent: (item: T) => JSX.Element,
  t: TUseTranslationTfn
): ITableFields<T>[] {
  return [
    {
      name: t("firm_name"),
      key: "firm_name",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-2",
    },
    {
      name: t("package"),
      key: "subscription_package_name",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-2",
      assistiveText: t("subscription_package_name"),
    },
    {
      name: t("purchased_date"),
      key: "subscription_date",
      sort: false,
      type: TABLE_FIELD_TYPE.date,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-2",
    },
    {
      name: t("expiry_date"),
      key: "end_date",
      sort: false,
      type: TABLE_FIELD_TYPE.date,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-2",
    },
    {
      name: t("expiry_days"),
      key: "remaining_expiry_days",
      sort: false,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.right,
      flex: "col-span-1",
    },
    {
      name: t("action"),
      key: "action",
      sort: false,
      type: TABLE_FIELD_TYPE.custom,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-2",
      component: actionComponent,
    },
  ];
}

export const getAllFirmPurchasedSubscription = async (
  setFirmPurchasedSubscriptionList: TReactSetState<
    IFirmPurchasedSubscription[]
  >,
  pagination: IPagination,
  setPagination: TSetPaginationFn,
  search: string,
  advanceSearch: IFirmPurchasedSubscriptionSearch
) => {
  try {
    const firmPurchasedSubscription = await GET_ALL_FIRM_PURCHASED_SUBSCRIPTION(
      pagination,
      search,
      advanceSearch
    );
    if (
      firmPurchasedSubscription &&
      firmPurchasedSubscription.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      setFirmPurchasedSubscriptionList(firmPurchasedSubscription.data.items);
      setPagination(firmPurchasedSubscription.data.pagination);
    } else {
      setFirmPurchasedSubscriptionList([]);
      sweetAlertError({
        message:
          firmPurchasedSubscription.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (error) {
    handleError(error, "getAllFirmPurchasedSubscription");
  }
};

export const getFirmNameDdl = async (
  setFirmName: TReactSetState<IOption<string>[]>,
  includeDeletedFirm: boolean = true
) => {
  try {
    const payload = {
      entity: "firms",
      data_value: "id",
      display_value: "firm_name",
      ...(includeDeletedFirm && {
        filters: [
          {
            field: "is_deleted",
            value: "1|0",
            condition: "eq",
          },
        ],
        additional_field: ["is_deleted"],
      }),
    };
    const firmNameDDLList = await GLOBAL_DROP_DOWN(payload);
    if (
      firmNameDDLList &&
      firmNameDDLList.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      if (firmNameDDLList.data) {
        setFirmName(firmNameDDLList.data);
      } else {
        setFirmName([]);
      }
    } else {
      toastError(firmNameDDLList.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    }
  } catch (e: any) {
    toastError(e?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    handleError(e, "getFirmNameDdl");
  }
};

export const cancelFirmSubscription = async (id: string) => {
  try {
    const cancelledSubscription = await CANCEL_SUBSCRIPTION(id);
    if (cancelledSubscription?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({ message: cancelledSubscription.message });
      return true;
    }
    sweetAlertError({
      message: cancelledSubscription?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
    });
    return false;
  } catch (e) {
    handleError(e, "cancelFirmSubscription");
    return false;
  }
};
